/* CHILL slide-out cart: opens from the header cart and after quick-add, and edits lines in place.
   All totals come back from Shopify through the Section Rendering API (section "cart-drawer"). */
(() => {
  'use strict';
  if (window.ChillCartDrawerReady) return;
  window.ChillCartDrawerReady = true;

  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const SECTION = 'cart-drawer';
  const drawer = () => document.getElementById('CartDrawer');
  let returnFocus = null;
  let refreshing = null;

  const onCartPage = () => location.pathname.replace(/\/$/, '') === `${ROOT}cart`.replace(/\/+/g, '/').replace(/\/$/, '');

  function announce(message) {
    const node = document.getElementById('SiteStatus');
    if (node) node.textContent = message;
  }
  function setError(message) {
    const node = drawer()?.querySelector('[data-cart-drawer-error]');
    if (!node) return;
    node.textContent = message || '';
    node.hidden = !message;
  }

  function swap(html) {
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('[data-cart-drawer-inner]');
    const dialog = drawer();
    const current = dialog?.querySelector('[data-cart-drawer-inner]');
    if (!fresh || !current) return;
    current.replaceWith(fresh);
    const count = Number(fresh.dataset.count);
    if (Number.isFinite(count)) {
      const badge = dialog.querySelector('[data-cart-drawer-count]');
      if (badge) badge.textContent = count;
      document.querySelectorAll('.cart-count').forEach((el) => {
        el.textContent = count;
        el.classList.toggle('is-empty', count < 1);
      });
    }
  }

  function refresh() {
    if (refreshing) return refreshing;
    refreshing = fetch(`${ROOT}?sections=${SECTION}`, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('refresh'))))
      .then((data) => swap(data[SECTION]))
      .catch(() => {})
      .finally(() => { refreshing = null; });
    return refreshing;
  }

  function open(opener) {
    const dialog = drawer();
    if (!dialog || typeof dialog.showModal !== 'function') return false;
    if (!dialog.open) {
      returnFocus = opener || document.activeElement;
      dialog.showModal();
    }
    dialog.querySelector('[data-cart-drawer-close]')?.focus({ preventScroll: true });
    return true;
  }
  function close() { drawer()?.close(); }

  async function changeLine(line, quantity) {
    const key = line?.dataset.key;
    if (!key) return;
    line.classList.add('is-busy');
    setError('');
    try {
      const response = await fetch(`${ROOT}cart/change.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity, sections: [SECTION] })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.status) {
        setError(data.description || data.message || 'Shopify could not accept that quantity.');
        await refresh();
        return;
      }
      if (data.sections && data.sections[SECTION]) swap(data.sections[SECTION]);
      else await refresh();
      announce(quantity === 0 ? 'Item removed from cart.' : 'Cart updated.');
    } catch (_) {
      setError('We could not update your cart. Check your connection and try again.');
      line.classList.remove('is-busy');
    }
  }

  function clampQuantity(input, value) {
    const min = Number(input.min) || 1;
    const step = Number(input.step) || 1;
    const max = input.max === '' ? Infinity : Number(input.max);
    let next = Math.max(min, Math.min(max, value));
    const offset = (next - min) % step;
    if (offset) next -= offset;
    return next;
  }

  function setup() {
    const dialog = drawer();
    if (!dialog) return;
    document.documentElement.classList.add('has-cart-drawer');
    if (dialog.parentElement !== document.body) document.body.append(dialog);

    dialog.addEventListener('close', () => {
      setError('');
      if (returnFocus && document.contains(returnFocus)) returnFocus.focus({ preventScroll: true });
      returnFocus = null;
    });

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) {
        const r = dialog.getBoundingClientRect();
        if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) close();
        return;
      }
      if (event.target.closest('[data-cart-drawer-close]')) { close(); return; }
      const line = event.target.closest('[data-cart-line]');
      if (!line) return;
      if (event.target.closest('[data-cart-remove]')) { changeLine(line, 0); return; }
      const step = event.target.closest('[data-cart-step]');
      if (step && !step.disabled) {
        const input = line.querySelector('[data-cart-qty]');
        const by = (Number(input.step) || 1) * Number(step.dataset.cartStep);
        const next = clampQuantity(input, (Number(input.value) || 0) + by);
        if (next !== Number(input.value)) { input.value = next; changeLine(line, next); }
      }
    });

    let typing;
    dialog.addEventListener('input', (event) => {
      const input = event.target.closest('[data-cart-qty]');
      if (!input) return;
      clearTimeout(typing);
      typing = setTimeout(() => input.dispatchEvent(new Event('change', { bubbles: true })), 700);
    });
    dialog.addEventListener('change', (event) => {
      const input = event.target.closest('[data-cart-qty]');
      if (!input) return;
      clearTimeout(typing);
      const line = input.closest('[data-cart-line]');
      const raw = Number(input.value);
      if (input.value.trim() === '' || !Number.isFinite(raw)) { refresh(); return; }
      if (raw <= 0) { changeLine(line, 0); return; }
      const next = clampQuantity(input, raw);
      input.value = next;
      changeLine(line, next);
    });
    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' && event.target.matches('[data-cart-qty]')) {
        event.preventDefault();
        event.target.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    // Header cart link opens the drawer (except on the cart page itself, or with a modifier key).
    document.addEventListener('click', (event) => {
      const link = event.target.closest('a.cart-link');
      if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (onCartPage()) return;
      event.preventDefault();
      open(link);
      refresh();
    });

    // Quick add (chill-catalog.js) shows a toast after a successful add; open the drawer instead.
    const watchToast = (toast) => {
      if (toast.dataset.cartDrawerWatched) return;
      toast.dataset.cartDrawerWatched = 'true';
      new MutationObserver(() => {
        if (toast.classList.contains('chill-toast--error') || !toast.childElementCount) return;
        if (onCartPage()) { location.reload(); return; }
        refresh().then(() => open());
      }).observe(toast, { childList: true });
    };
    const existing = document.querySelector('.chill-toast');
    if (existing) watchToast(existing);
    new MutationObserver((records) => {
      records.forEach((record) => record.addedNodes.forEach((node) => {
        if (node.nodeType !== 1 || !node.classList.contains('chill-toast')) return;
        watchToast(node);
        if (!node.classList.contains('chill-toast--error') && node.childElementCount) {
          if (onCartPage()) { location.reload(); return; }
          refresh().then(() => open());
        }
      }));
    }).observe(document.body, { childList: true });

    // Back/forward cache can restore a stale cart.
    window.addEventListener('pageshow', (event) => { if (event.persisted) refresh(); });
  }

  window.ChillCartDrawer = Object.freeze({ open, close, refresh });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', setup);
  else setup();
})();
